import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Refresh button phases (design 40): the square icon button presses, widens into an "Updating…"
 * pill, confirms with "Updated" and shrinks back, or stays as "Update failed" until the next click.
 */
export type RefreshPhase = "idle" | "press" | "busy" | "done" | "failed";

const PRESS_MS = 80;
const MIN_BUSY_MS = 500;
const DONE_MS = 1_200;

/**
 * Drives the phases from the caller's own request state. `pending` is the request in flight and
 * `failed` whether the last one ended in an error; both are only read while the pill is busy, so a
 * background poll never animates the button. `start()` is false when a click must be ignored.
 */
export function useRefreshPhase({ pending, failed }: { pending: boolean; failed: boolean }) {
  const [phase, setPhase] = useState<RefreshPhase>("idle");
  // Clicks are refused synchronously: two clicks in one tick would otherwise both start.
  const active = useRef(false);
  const busySince = useRef(0);
  const failedRef = useRef(failed);
  failedRef.current = failed;

  useEffect(() => {
    if (phase !== "press") return;
    const timer = window.setTimeout(() => {
      busySince.current = Date.now();
      setPhase("busy");
    }, PRESS_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "busy" || pending) return;
    // The pill holds its busy frame for at least MIN_BUSY_MS even when the response was faster.
    const wait = Math.max(0, busySince.current + MIN_BUSY_MS - Date.now());
    const timer = window.setTimeout(() => {
      active.current = false;
      setPhase(failedRef.current ? "failed" : "done");
    }, wait);
    return () => {
      window.clearTimeout(timer);
    };
  }, [phase, pending]);

  useEffect(() => {
    if (phase !== "done") return;
    const timer = window.setTimeout(() => {
      setPhase("idle");
    }, DONE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [phase]);

  const start = useCallback(() => {
    if (active.current) return false;
    active.current = true;
    setPhase("press");
    return true;
  }, []);

  return { phase, start };
}
